import { useSystemHealth, type HealthDependency } from '@theraply/api-client';

function formatUptime(ms: number): string {
  if (ms <= 0) return 'N/A';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h ${minutes % 60}m`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
}

function formatTimestamp(ts: string): string {
  try {
    return new Date(ts).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return ts;
  }
}

function StatusBadge({ status }: { status: 'UP' | 'DOWN' | string }) {
  const isUp = status === 'UP';
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
        isUp
          ? 'bg-green-100 text-green-800'
          : 'bg-red-100 text-red-800'
      }`}
    >
      <span
        className={`mr-2 h-2.5 w-2.5 rounded-full ${
          isUp ? 'bg-green-500' : 'bg-red-500'
        }`}
      />
      {status}
    </span>
  );
}

function DependencyRow({ dep }: { dep: HealthDependency }) {
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900 capitalize">{dep.name}</div>
        <div className="text-sm text-gray-500">{dep.type}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <StatusBadge status={dep.status} />
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {dep.responseTime || 'N/A'}
      </td>
    </tr>
  );
}

export default function SystemHealthPage() {
  const { data, isLoading, error, dataUpdatedAt, refetch } = useSystemHealth(30000);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking system health...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Error</h3>
          <p className="text-red-600">
            {error instanceof Error ? error.message : 'Failed to fetch system health'}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const overallStatus = data?.status || 'DOWN';
  const isUp = overallStatus === 'UP';

  return (
    <div>
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">System Health</h1>
          <p className="mt-1 text-sm text-gray-500">
            Monitor system performance and infrastructure health. Auto-refreshes every 30 seconds.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Refresh Now
        </button>
      </div>

      {/* Overall Status Card */}
      <div className="mt-8">
        <div
          className={`rounded-lg shadow p-6 ${
            isUp ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Overall Status</h2>
              <p className="text-sm text-gray-600 mt-1">
                {data?.service || 'theraply-backend'}
              </p>
            </div>
            <StatusBadge status={overallStatus} />
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-500">Uptime</div>
          <div className="mt-2 text-2xl font-bold text-gray-900">
            {data?.uptime ? formatUptime(data.uptime) : 'N/A'}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-500">Last Check</div>
          <div className="mt-2 text-lg font-semibold text-gray-900">
            {data?.timestamp ? formatTimestamp(data.timestamp) : 'N/A'}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-500">Last Refreshed</div>
          <div className="mt-2 text-lg font-semibold text-gray-900">
            {dataUpdatedAt ? formatTimestamp(new Date(dataUpdatedAt).toISOString()) : 'N/A'}
          </div>
        </div>
      </div>

      {/* Dependencies Table */}
      <div className="mt-8 bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Dependencies</h2>
          <p className="text-sm text-gray-500 mt-1">
            Status of backend service dependencies
          </p>
        </div>
        <div className="overflow-x-auto">
          {!data?.dependencies || data.dependencies.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No dependency details available. The detailed health endpoint may be running on a separate port.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Response Time
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.dependencies.map((dep) => (
                  <DependencyRow key={dep.name} dep={dep} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* API Endpoint Status */}
      <div className="mt-8 bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">API Status</h2>
          <p className="text-sm text-gray-500 mt-1">
            Main API server connectivity
          </p>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-gray-900">Main API Server</div>
              <div className="text-sm text-gray-500">HTTP health endpoint</div>
            </div>
            <StatusBadge status={data ? 'UP' : 'DOWN'} />
          </div>
        </div>
      </div>
    </div>
  );
}
